// ============================================================================================
// NOS APPLICATIONS INSTALLABLES — la liste, la seule : ce que chacune est, son nom au store privé, son état.
//
// UTILITÉ : un appareil du parc (caisse, terminal) installe une application que le store privé distribue sous un
// NOM (son slug). Ce nom était écrit à la main à sept endroits que rien ne comparait (cadrage
// `CADRAGE-Applications-Store.md` §1), et la production l'a payé : son store portait Mobile Partner sous un autre
// nom que celui où la construction dépose l'APK. Ici vit la liste ; tout le reste la LIT.
//
// QUI LA LIT :
//   · la Console — types d'équipement, groupes de mise à jour d'un magasin, assistant de création, et l'écran
//     Applications, qui la rapproche du store de son environnement et dit, pour chacune, si elle y est ;
//   · la construction — `deploy/apps-android.tsv` est GÉNÉRÉ depuis ce fichier (`scripts/build-apps-android.mjs`),
//     parce que le workflow et les scripts de build lisent un fichier de données, pas du TypeScript. Sa fraîcheur
//     est gardée au lint (`audit-apps-android.mjs`).
//
// ⚠️ UN SEUL NOM PAR APPLICATION, IDENTIQUE DANS TOUS LES STORES (décision utilisateur). La construction
//    dépose sous ce nom, en bêta comme en production, sans demander à personne : une application créée au store
//    sous un autre nom n'y recevra jamais d'APK. Le nom est FIGÉ au store (l'administration crée, ne renomme pas),
//    donc on ne change jamais un slug ici — il est déjà persisté dans les types d'équipement et les groupes choisis.
// ⚠️ LA LISTE PORTE AUSSI CE QUI N'EXISTE PAS ENCORE : un produit annoncé a sa ligne, avec l'état `a-venir`, pour que
//    son nom soit décidé une fois et visible de tous avant la première construction. Ce qui ne s'installe pas
//    (`isInstallable`) ne se propose nulle part comme un choix qui agirait (§23.0).
// ============================================================================================

/**
 * Où en est une application.
 *   · `prod`     — construite et distribuée aux appareils ;
 *   · `echafaud` — le projet natif compile, aucun écran métier : l'APK s'installe et ne fait rien d'autre ;
 *   · `reserve`  — le paquet est posé, sans projet natif : rien ne se construit ;
 *   · `a-venir`  — le produit est décidé, aucun code au dépôt.
 */
export type AppState = 'prod' | 'echafaud' | 'reserve' | 'a-venir';

/**
 * L'appareil qui installe l'application — la NATURE d'un type d'équipement de la Console (`equipment-types.ts`).
 * `null` : à définir, le produit n'a pas encore d'appareil décidé.
 */
export type AppDeviceNature = 'caisse' | 'mobile-partner';

/** Une de nos applications. */
export interface PlatformApplication {
  /** Le nom au store privé — celui sous lequel la construction dépose l'APK, dans TOUS les stores. */
  slug: string;
  /** Le nom du produit. */
  name: string;
  /** Ce qu'elle est, en clair. Une seule ligne : il devient la note de `deploy/apps-android.tsv`. */
  description: string;
  state: AppState;
  /** Le dossier de l'application au dépôt ; `null` tant qu'aucun code n'existe (`a-venir`). */
  dir: string | null;
  nature: AppDeviceNature | null;
}

// ⚠️ L'ORDRE est celui de l'affichage et du fichier généré : on ajoute en fin de liste, on ne réordonne pas (le diff
//    du fichier généré doit dire ce qui a changé, pas tout redire).
export const APPS = {
  retail: {
    slug: 'sliderz-retail',
    name: 'Sliderz Retail',
    description: 'Caisse tablette React Native — en production, distribuée par le store privé',
    state: 'prod',
    dir: 'apps/sliderz/retail',
    nature: 'caisse',
  },
  chr: {
    slug: 'sliderz-chr',
    name: 'Sliderz CHR',
    description: 'Caisse CHR React Native — paquet réservé, ni android/ ni écrans (la maquette CHR existe depuis le 2026-10-13, rien n’est construit)',
    state: 'reserve',
    dir: 'apps/sliderz/chr',
    nature: 'caisse',
  },
  mobilePartner: {
    slug: 'sliderz-mobile-partner',
    name: 'Sliderz Mobile Partner',
    description: 'Terminal durci de stock au scan (inventaire, réception, transfert, consultations) — écrans codés, distribué par le store privé',
    state: 'prod',
    dir: 'apps/mobile-partner',
    nature: 'mobile-partner',
  },
  telephony: {
    slug: 'sliderz-telephonie',
    name: 'Sliderz Téléphonie',
    description: 'Application du métier de la téléphonie — annoncée, aucun code au dépôt, appareil à définir',
    state: 'a-venir',
    dir: null,
    nature: null,
  },
} as const satisfies Record<string, PlatformApplication>;

/** Toutes nos applications, dans l'ordre de la liste. */
export const APPLICATIONS: readonly PlatformApplication[] = Object.values(APPS);

/** Le nom au store de l'une de nos applications. */
export type AppSlug = (typeof APPS)[keyof typeof APPS]['slug'];

/** L'application de ce nom, ou `null` — comparaison EXACTE : un nom au store est un identifiant. */
export function applicationOf(slug: string): PlatformApplication | null {
  return APPLICATIONS.find((a) => a.slug === slug) ?? null;
}

/**
 * Une application qu'un appareil peut installer : un APK se construit et se dépose au store.
 * ⚠️ `echafaud` en est : une coquille s'installe sur un appareil d'essai. `reserve` et `a-venir` n'en sont pas —
 *    proposer de les choisir ferait attendre une mise à jour qui n'arrivera jamais.
 */
export function isInstallable(app: PlatformApplication): boolean {
  return app.state === 'prod' || app.state === 'echafaud';
}

/** Les applications installables, dans l'ordre de la liste. */
export function installableApplications(): PlatformApplication[] {
  return APPLICATIONS.filter(isInstallable);
}

/** Les applications installables qu'un appareil de cette nature reçoit. */
export function installableFor(nature: AppDeviceNature): PlatformApplication[] {
  return installableApplications().filter((a) => a.nature === nature);
}

/**
 * Un nom de produit ou d'application ramené à ses lettres : minuscules, sans accents ni ponctuation, sans le préfixe de
 * la marque — « Sliderz Mobile Partner », « Mobile Partner » et `sliderz-mobile-partner` deviennent tous « mobile partner ».
 * ⚠️ UNE SEULE RÈGLE pour le store et la Console : le store s'en sert pour proposer le nom exact à la création, la Console
 *    pour reconnaître une application portée sous un autre nom. Écrite deux fois, elle divergerait.
 */
export function bareName(s: string): string {
  return s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim().replace(/^sliderz(?: |$)/, '');
}

/**
 * L'une de nos applications par son nom de PRODUIT, tel qu'un humain le tape (« Mobile Partner »), ou `null`.
 * ⚠️ Le store dérivait le nom technique du nom affiché : « Mobile Partner » y donnait `mobile-partner`, que la construction
 *    ne connaît pas (le store de production l'a porté). Reconnaître le produit rend le nom exact.
 * ⚠️ Un nom vide (ou la marque seule) ne désigne rien parce qu'AUCUNE application n'a un nom vide sans la marque — la liste
 *    le refuse à la source (`listProblems`), et c'est là que la règle tient, pas ici.
 */
export function applicationNamed(name: string): PlatformApplication | null {
  const b = bareName(name);
  return APPLICATIONS.find((a) => bareName(a.name) === b) ?? null;
}

/** Le libellé d'un état, pour l'affichage. */
export const APP_STATE_TEXT: Record<AppState, string> = {
  prod: 'En production',
  echafaud: 'Échafaudage — s’installe, ne fait rien',
  reserve: 'Réservée — rien à construire',
  'a-venir': 'À venir — aucun code',
};

/**
 * Le format d'un nom d'application, tel que le store l'accepte à la création (`POST /admin/apps`).
 * ⚠️ Recopié du store et non importé : le store n'est pas un paquet. La garde de la liste (`listProblems`) le vérifie
 *    sur chaque nom, et le banc du store vérifie que les deux s'accordent.
 */
export const STORE_SLUG_RE = /^[a-z0-9][a-z0-9-]*$/;

/**
 * Ce qui rend la liste inutilisable, ligne par ligne — vide quand elle est saine.
 * ⚠️ Une donnée mal formée ici se propagerait au fichier de la construction ET à la Console : on la refuse à la
 *    source, avec le nom de la ligne fautive.
 */
export function listProblems(apps: readonly PlatformApplication[] = APPLICATIONS): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const a of apps) {
    if (!STORE_SLUG_RE.test(a.slug)) out.push(`« ${a.slug} » : nom refusé par le store (minuscules, chiffres, tirets).`);
    if (seen.has(a.slug)) out.push(`« ${a.slug} » : déclaré deux fois.`);
    seen.add(a.slug);
    if (!a.name.trim()) out.push(`« ${a.slug} » : sans nom de produit.`);
    // ⚠️ Un nom qui n'est QUE la marque se ramène au vide : le store le reconnaîtrait dans n'importe quel libellé vide.
    else if (bareName(a.name) === '') out.push(`« ${a.slug} » : son nom de produit n’est que la marque (« ${a.name} ») — le store ne pourrait pas le reconnaître.`);
    if (/[\t\r\n]/.test(a.description) || /[\t\r\n]/.test(a.name)) out.push(`« ${a.slug} » : tabulation ou retour à la ligne — le fichier de la construction est découpé par tabulations.`);
    if (a.state === 'a-venir' && a.dir !== null) out.push(`« ${a.slug} » : à venir, mais un dossier est déclaré (${a.dir}) — l’état ne dit plus la vérité.`);
    if (a.state !== 'a-venir' && a.dir === null) out.push(`« ${a.slug} » : sans dossier au dépôt — seule une application à venir n’en a pas.`);
    if (isInstallable(a) && a.nature === null) out.push(`« ${a.slug} » : installable, mais aucun appareil ne la reçoit (nature à définir).`);
  }
  return out;
}
